import { RolesRolesOptions } from '~/composables/pocketbase.types';
import { Ref, computed } from 'vue';
import { useTournamentsStore } from './tournamentsStore';

export type Roles = `${RolesRolesOptions}`;

export type CurrentUser = {
  name: string;
  id: string;
  player: object | null;
  team: object | null;
  roles: RolesRolesOptions[];
  teammanager_team: object | null;
};

export const useUsersStore = defineStore('users', () => {
  const pb = usePocketBase();
  const currentUser: Ref<null | CurrentUser> = ref(null);

  const isActiveTeammanager = computed(() =>
    !!currentUser.value?.roles?.includes('teammanager') && !!currentUser.value?.player,
  );

  async function verifyAuthAgainstServer() {
    await pb
      .collection('users')
      .authRefresh()
      .then(() => {
        return true;
      })
      .catch((error) => {
        pb.authStore.clear();
        // refreshCurrentUser is not needed, because the onChange listener calls it for us
        return false;
      });
    return true;
  }

  async function refreshCurrentUser() {
    if (pb.authStore.isValid) {
      let roles: RolesRolesOptions[] = [];
      try {
        const data = await pb.collection('roles').getFullList({
          filter: `user="${pb.authStore.record?.id}"`,
          requestKey: null,
        });
        if (data?.length > 0) {
          roles = data[0]?.roles;
        }
      } catch (error) {
        console.log('Error: ', error);
      }
      const player = ref(null);
      if (pb.authStore.record?.player !== '') {
        const tournamentsStore = useTournamentsStore();
        const playerRecord = await pb
          .collection('players')
          .getOne(pb.authStore.record?.player, { expand: ['team'] });
        if (playerRecord.tournament === tournamentsStore.activeTournamentId) {
          player.value = playerRecord;
        }
      }
      currentUser.value = {
        name: pb.authStore.record?.name,
        id: pb.authStore.record?.id,
        player: player?.value,
        roles: roles,
        team: player.value?.expand?.team,
      };
    } else {
      currentUser.value = null;
    }
  }

  refreshCurrentUser();
  pb.authStore.onChange(async () => await refreshCurrentUser());

  return {
    currentUser: currentUser,
    isActiveTeammanager,
    refreshCurrentUser: refreshCurrentUser,
    verifyAuthAgainstServer: verifyAuthAgainstServer,
  };
});
